import { useEffect, useState } from "react";

function getOnline(): boolean {
  if (typeof navigator === "undefined") return true;
  return navigator.onLine;
}

/**
 * Returns true when the browser believes it has network connectivity.
 * Listens to the "online" and "offline" window events, and also re-checks
 * navigator.onLine when the document becomes visible again (e.g. after the
 * user switches back from another tab).
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState<boolean>(getOnline);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        setOnline(getOnline());
      }
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return online;
}
